import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import translate from '../../commonComponents/translate';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import {
  isEnrolledOrFree,
  PAYMENT_METHOD,
} from '../../services/utilityFunctions';
import CourseProp from './propTypes/courseProp';
import AcademyCourse from './list/AcademyCourse';
import CourseContentDetails from './details/CourseContentDetails';
import RelatedCourses from './details/RelatedCourses';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import AppProductProp from '../profile/propTypes/appProductProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../profile/propTypes/StripeCardProp';
import AcademyCourseMoreInfo from './list/AcademyCourseMoreInfo';
import ScormDetail from './details/ScormDetail';
import AcademyPaymentModal from '../../commonComponents/modal/AcademyPaymentModal';
import CurrencyProp from '../profile/propTypes/currencyProp';
import { useModal } from '../../services/customHooks';
import TenantProp from '../../commonComponents/propTypes/tenantProp';
import VatTypesProp from '../../commonComponents/forms/Address/VatTypesProp';
import ShopModalOrderComplete from '../shop/shopItems/ShopModalOrderComplete';
import ContentInacessibleModal from '../../commonComponents/modal/ContentInacessibleModal';

function AcademyDetails(props) {
  const {
    t,
    course: courseProp,
    related,
    showCertificate,
    cards,
    countries,
    appProducts,
    startPlaying,
    languages,
    pspdfkitLicenseKey,
    currencies,
    orderId,
    redirect_status,
    payment_intent,
    open,
    sessionContext,
    tenant,
    vatTypes,
  } = props;
  const { loggedUser } = useContext(LoggedUserContext);
  const isEnrolled = isEnrolledOrFree(loggedUser, courseProp);

  const [shouldShowModal, setShowModal] = useModal({
    show: !!(orderId && redirect_status && payment_intent) || !!open,
    data: null,
  });
  const [showContentInacessibleModal, setShowContentInacessibleModal] =
    useModal({
      show: false,
      data: null,
    });
  const showModal = () => {
    if (courseProp.isContentAccessible) {
      setShowModal({ show: true, data: null });
    } else {
      setShowContentInacessibleModal({ show: true, data: null });
    }
  };
  const hideModal = () => {
    setShowModal({ show: false, data: null });
  };

  const [course, setCourse] = useState(courseProp);

  const [scrollContent, setScrollContent] = useState(false);
  const forceScrollContent = () => setScrollContent(prev => !prev);

  const [shouldStartPlaying, setShouldStartPlaying] = useState(startPlaying);
  const handleCoursePlay = () => {
    setShouldStartPlaying(true);
    forceScrollContent();
  };

  const itemToPay = {
    name: courseProp.name,
    userPromotionPrice: courseProp.userPromotionPrice,
    userPrice: courseProp.userPrice,
    _id: courseProp._id,
    image: courseProp.image,
  };
  const typeOfItem = {
    field: 'courses',
    fieldId: 'course',
  };
  const [showOrderSuccessModal, setShowOrderSuccessModal] = useModal({
    show: false,
    data: null,
  });

  const browserHistory = useHistory();
  const handleReload = () => {
    browserHistory.replace(`${window.location.pathname}`);
    browserHistory.go(0);
  };

  const handleSuccessModal = paymentMethod => {
    setShowModal({ show: false, data: null });
    let urlGoBack = '/academy';
    let urlGoToOrder = '/shop/academy/order';

    let message =
      paymentMethod === PAYMENT_METHOD.PAID_BY_COMPANY
        ? t('academy.modal.success.paid_by_company', t('course'))
        : t('academy.modal.success.bankTransfer', t('course'));

    // It will unlock if payment was completed or if the course, has `contentAvailableBeforePayment` as `true`
    const isUnlocked =
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD ||
      paymentMethod === PAYMENT_METHOD.BANCONTACT ||
      paymentMethod === PAYMENT_METHOD.PAYPAL ||
      redirect_status === 'succeeded' ||
      courseProp.contentAvailableBeforePayment;
    if (isUnlocked) {
      message = t('academy.modal.success.order', t('course'));
      urlGoBack = null;
      urlGoToOrder = null;
    }
    setTimeout(() => {
      setShowOrderSuccessModal({
        show: true,
        data: {
          urlGoBack,
          urlGoToOrder,
          modalMessage: message,
          onCloseModal: isUnlocked ? handleReload : () => {},
          buttonMessage: isUnlocked ? t('ok') : null,
          showBack: !isUnlocked,
        },
      });
    }, 1500);
  };

  return (
    <Title title={t('academyDetails_head_title')}>
      <ContentContainer>
        {showOrderSuccessModal.show && (
          <ShopModalOrderComplete
            closeModal={showOrderSuccessModal.data?.onCloseModal}
            urlGoToOrder={showOrderSuccessModal?.data?.urlGoToOrder}
            urlGoBack={showOrderSuccessModal?.data?.urlGoBack}
            goBackMessage={t('order.success.goBack', t('courses'))}
            modalMessage={showOrderSuccessModal?.data?.modalMessage}
            confirmMessage={showOrderSuccessModal?.data?.buttonMessage}
            showBack={showOrderSuccessModal?.data?.showBack}
          />
        )}
        {shouldShowModal.show && (
          <AcademyPaymentModal
            item={itemToPay}
            typeOfItem={typeOfItem}
            onClose={hideModal}
            cards={cards}
            currencies={currencies}
            countries={countries}
            orderId={orderId}
            redirectStatus={redirect_status}
            paymentIntent={payment_intent}
            tenant={tenant}
            vatTypes={vatTypes}
            openSuccessModal={handleSuccessModal}
          />
        )}
        {showContentInacessibleModal.show && (
          <ContentInacessibleModal
            closeModal={() =>
              setShowContentInacessibleModal({ show: false, data: null })
            }
          />
        )}
        <SubHeader
          title={t('academyDetails_title', course.name)}
          breadcrumbs={[
            { link: '/academy', text: t('header_academy') },
            { link: '', text: t('header_academy_courseDetails') },
          ]}
        />
        <div className="m-content">
          <div className="row full-page-container-with-subheader">
            <div className="col-lg-12">
              <AcademyCourse
                t={t}
                course={course}
                languages={languages}
                onEnroll={showModal}
                showEnroll={!isEnrolled}
                showModules
                showDetails={false}
                showCertificate
                onCourseContinue={forceScrollContent}
                onCoursePlay={handleCoursePlay}
              />
              <AcademyCourseMoreInfo course={course} />
              {!course.isFromExternalProvider && (
                <CourseContentDetails
                  shouldScrollContent={scrollContent}
                  onEnroll={showModal}
                  course={course}
                  gameId={course.gameId}
                  onCourseChange={setCourse}
                  cards={cards}
                  appProducts={appProducts}
                  countries={countries}
                  openInCertificate={showCertificate}
                  startPlaying={shouldStartPlaying}
                  pspdfkitLicenseKey={pspdfkitLicenseKey}
                  sessionContext={sessionContext}
                />
              )}
              {course.isFromExternalProvider && (
                <ScormDetail
                  shouldScrollContent={scrollContent}
                  showEnrollOverlay={!isEnrolled}
                  onEnroll={showModal}
                  course={course}
                  title={course.name}
                  url={course.provider.config.scorm.urlUser || ''}
                  cards={cards}
                  appProducts={appProducts}
                  countries={countries}
                  sessionContext={sessionContext}
                />
              )}
              <RelatedCourses courses={related} />
            </div>
          </div>
          <div className="row back-btn-container">
            <button
              type="button"
              className="btn default button-previous"
              onClick={() => {
                window.location = '/academy?applyFilters=true';
              }}
            >
              <i className="fa fa-angle-left" />
              {` ${t('back')}`}
            </button>
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

AcademyDetails.propTypes = {
  t: PropTypes.func.isRequired,
  course: PropTypes.shape(CourseProp.propType).isRequired,
  related: PropTypes.arrayOf(PropTypes.shape(CourseProp.propType)).isRequired,
  startPlaying: PropTypes.bool,
  showCertificate: PropTypes.bool,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  appProducts: PropTypes.arrayOf(PropTypes.shape(AppProductProp.propType))
    .isRequired,
  pspdfkitLicenseKey: PropTypes.string,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  orderId: PropTypes.string,
  redirect_status: PropTypes.string,
  payment_intent: PropTypes.string,
  open: PropTypes.bool,
  sessionContext: PropTypes.string,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
};

AcademyDetails.defaultProps = {
  showCertificate: false,
  startPlaying: false,
  pspdfkitLicenseKey: null,
  orderId: null,
  redirect_status: null,
  payment_intent: null,
  open: null,
  sessionContext: null,
};

export default translate(AcademyDetails);
